import { alpha } from '@precis-digital/kurama/utils'
import { type CSSObject, type Theme, type ThemeColor } from '@precis-digital/kurama/theme'
import { type RechartsMargin as Margin } from '@precis-digital/kurama/recharts'
import type {
  ColumnFiltersState,
  ColumnOrderState,
  ColumnPinningPosition,
  ColumnPinningState,
  ColumnSizingState,
  Row,
  SortDirection,
  SortingState
} from '@tanstack/react-table'
import { flatten, uniq } from 'ramda'
import { isValidElement, type ReactElement, type ReactNode } from 'react'
import { CHART_CARD_TABLE_LAYOUT } from '../../../constants'
import type {
  ElementDescriptionDimension,
  ElementDescriptionMetric,
  ElementsDescriptionsMap,
  MetricChoice,
  MetricsMap,
  TotalsAndRangeData
} from '../../../internalTypes'
import type {
  ChartCardActionOnClick,
  ChartCardDefaults,
  ChartCardTableActionContext,
  ChartCardTableRowActionContext,
  ChartDataRow,
  ChartDataValue
} from '../../../types'
import { elementDescriptionIsDimension, elementDescriptionIsMetric } from '../../../utils/elementDescription'
import { aggregateMetricRespectingRecalculate } from '../../../utils/data'
import { assertNever, errorLocal, formatDateValue, formatMetricValue, getDimensionLabel } from '../../../utils/generics'
import ChartBadge from '../../ChartBadge'
import { ROW_SELECTION_COLUMN_ID, Z_INDEX } from '../constants'
import {
  StyledBodyText,
  StyledCellContainer,
  StyledHeaderLabel,
  StyledImage,
  StyledPaddedDecorationContainer
} from '../styled'
import type {
  ChartCardTable,
  ChartCardTableAccessorFnColumnDef,
  ChartCardTableAccessorKeyColumnDef,
  ChartCardTableBodyCellContextResolved,
  ChartCardTableColumn,
  ChartCardTableColumnDef,
  ChartCardTableColumnDefBase,
  ChartCardTableHeaderContextResolved
} from '../tanstack'
import type {
  CellAlignment,
  ColorScaleColumnMap,
  ColumnColorScaleState,
  ColumnElementDescription,
  DragHandleBinding,
  FilteredTotal,
  HeaderLabelData,
  HeaderLabelDataWithElement,
  HeaderLabelDataWithoutElement,
  TableHeadCellProps
} from '../types'

const getColumnBackgroundColor = (
  defaults: ChartCardDefaults,
  theme: Theme,
  type: 'head' | 'body' | 'footer',
  rowHovered: boolean,
  pinned: ColumnPinningPosition
): ThemeColor | undefined => {
  if (pinned === false) {
    return rowHovered
      ? defaults.table.hoverBackgroundColor[type] ?? defaults.table.backgroundColor[type]
      : defaults.table.backgroundColor[type]
  }
  if (rowHovered) {
    return (
      defaults.table.hoverBackgroundColor[`${type}Pinned`] ??
      defaults.table.backgroundColor[`${type}Pinned`] ??
      defaults.table.hoverBackgroundColor[type] ??
      defaults.table.backgroundColor[type] ??
      theme.tokens.background.main.default
    )
  }

  return (
    defaults.table.backgroundColor[`${type}Pinned`] ??
    defaults.table.backgroundColor[type] ??
    theme.tokens.background.main.default
  )
}

export const getColumnCellStyle = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  defaults,
  theme,
  column,
  allowResizing,
  pinned,
  type,
  rowHovered
}: {
  defaults: ChartCardDefaults
  theme: Theme
  column: ChartCardTableColumn<DataKey, DataRow>
  allowResizing: boolean
  pinned: ColumnPinningPosition
  type: 'head' | 'body' | 'footer'
  rowHovered: boolean
}): CSSObject => {
  const zIndex = Z_INDEX[type]
  const backgroundColor = getColumnBackgroundColor(defaults, theme, type, rowHovered, pinned)

  let endPinned: ColumnPinningPosition = false
  if (pinned === 'left' && column.getIsLastColumn('left')) {
    endPinned = 'left'
  } else if (pinned === 'right' && column.getIsFirstColumn('right')) {
    endPinned = 'right'
  }

  const allowResizingShadow = allowResizing ? defaults.table.allowResizingShadow[type] : undefined
  const pinnedShadow = endPinned === false ? undefined : defaults.table.endPinnedShadow[type]?.[endPinned]
  const defaultShadow = defaults.table.shadows[type]
  const boxShadows = flatten([defaultShadow, allowResizingShadow, pinnedShadow].filter((shadow) => shadow != null))
  const boxShadow = uniq(boxShadows).join(', ')

  return {
    boxShadow,
    left: pinned === 'left' ? `calc(var(--pinned-left-${column.id}) * 1px)` : undefined,
    right: pinned === 'right' ? `calc(var(--pinned-right-${column.id}) * 1px)` : undefined,
    position: pinned === false ? 'relative' : 'sticky',
    zIndex: pinned === false ? zIndex : zIndex + 1,
    backgroundColor,
    ...(pinned !== false && { flexShrink: 0 })
  }
}

export const getColorScaleStyle = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  defaults: ChartCardDefaults,
  colorScale: ColumnColorScaleState | undefined,
  dataKey: DataKey | undefined,
  row: DataRow,
  totalsAndRangeData: TotalsAndRangeData<DataKey>,
  theme: Theme
): CSSObject | undefined => {
  if (colorScale == null || !colorScale.display || dataKey == null) {
    return undefined
  }
  const min = totalsAndRangeData.min[dataKey]
  const max = totalsAndRangeData.max[dataKey]
  const value = row[dataKey]

  if (min == null || max == null || min === max || typeof value !== 'number') {
    return undefined
  }

  if (value === 0 && min >= 0) {
    return undefined
  }

  const intensity =
    ((value - min) / (max - min)) * (defaults.table.colorScale.max - defaults.table.colorScale.min) +
    defaults.table.colorScale.min
  return {
    backgroundColor: alpha(colorScale.color ?? theme.tokens.background.brand.main.default, intensity)
  }
}

export const getSortDirectionForColumnId = (
  sorting: SortingState | undefined,
  columnId: string
): SortDirection | false => {
  const entry = sorting?.find((s) => s.id === columnId)
  if (entry == null) {
    return false
  }
  return entry.desc ? 'desc' : 'asc'
}

export const getPinPositionForColumnId = (
  columnPinning: ColumnPinningState | undefined,
  columnId: string
): ColumnPinningPosition | false => {
  if (columnPinning?.left?.includes(columnId) === true) {
    return 'left'
  }
  if (columnPinning?.right?.includes(columnId) === true) {
    return 'right'
  }
  return false
}

export const getFilterValueForColumnId = (columnFilters: ColumnFiltersState | undefined, columnId: string): unknown => {
  const entry = columnFilters?.find((filterState) => filterState.id === columnId)
  if (entry == null) {
    return undefined
  }
  return entry.value
}

export const getIsFilteredForColumnId = (columnFilters: ColumnFiltersState | undefined, columnId: string): boolean =>
  columnFilters?.find((filterState) => filterState.id === columnId) != null

const getSisingPropsFromColumnDef = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  column: ChartCardTableColumn<DataKey, DataRow>
): { isFixedSize: boolean; minSize: number | undefined; maxSize: number | undefined; size: number | undefined } => {
  const padding = CHART_CARD_TABLE_LAYOUT.padding.head
  const paddingChanges = { right: -5 }

  const {
    columnDef: { minSize: minSizeProp, maxSize: maxSizeProp, size: sizeProp, meta }
  } = column
  const isFixedSize = meta?.chartCard?.fixedSize === true
  const totalHorizontalPadding = calculateTotalHorizontalPadding(padding, isFixedSize ? undefined : paddingChanges)

  return {
    isFixedSize,
    minSize: minSizeProp == null ? undefined : minSizeProp + totalHorizontalPadding,
    maxSize: maxSizeProp == null ? undefined : maxSizeProp + totalHorizontalPadding,
    size: sizeProp == null ? undefined : sizeProp + totalHorizontalPadding
  }
}

export const clampColumnSizingToColumnDefs = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  table: ChartCardTable<DataKey, DataRow>,
  sizing: ColumnSizingState
): ColumnSizingState => {
  const fallbackMin = table.options.defaultColumn?.minSize ?? 48
  const fallbackMax = table.options.defaultColumn?.maxSize ?? Number.MAX_SAFE_INTEGER

  const next: ColumnSizingState = { ...sizing }

  for (const columnId of Object.keys(next)) {
    const value = next[columnId]
    if (typeof value !== 'number' || Number.isNaN(value)) {
      continue
    }
    const column = table.getColumn(columnId)
    if (column == null) {
      continue
    }

    const { minSize, maxSize, size, isFixedSize } = getSisingPropsFromColumnDef(column)

    const fixedSize = size ?? minSize ?? fallbackMin
    const min = (isFixedSize ? fixedSize : undefined) ?? Math.min(minSize ?? fallbackMin, fixedSize)
    const max = (isFixedSize ? fixedSize : undefined) ?? Math.max(maxSize ?? fallbackMax, fixedSize)

    next[columnId] = Math.min(max, Math.max(min, value))
  }
  return next
}

export const isColumnWithAccessorKey = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  column: ChartCardTableColumnDef<DataKey, DataRow>
): column is ChartCardTableAccessorKeyColumnDef<DataKey, DataRow> =>
  'accessorKey' in column && typeof column.accessorKey === 'string'

export const isColumnWithAccessorFn = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  column: ChartCardTableColumnDef<DataKey, DataRow>
): column is ChartCardTableAccessorFnColumnDef<DataKey, DataRow> =>
  'accessorFn' in column && typeof column.accessorFn === 'function'

const accessorKeyToColumnId = (accessorKey: string): string => accessorKey.replaceAll('.', '_')

export const getColumnId = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  column: ChartCardTableColumnDef<DataKey, DataRow>
): string => {
  if (column.id != null) {
    return column.id
  }
  if (isColumnWithAccessorKey(column)) {
    return accessorKeyToColumnId(column.accessorKey as string)
  }
  if (typeof column.header === 'string') {
    return column.header
  }
  throw new Error(
    'ChartCard table column needs column.id, accessorKey, or a string header (same rules as @tanstack/react-table).'
  )
}

export const getColumnDataKey = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  column: ChartCardTableColumnDef<DataKey, DataRow> | ChartCardTableColumn<DataKey, DataRow>
): DataKey | undefined => {
  if ('columnDef' in column) {
    return getColumnDataKey(column.columnDef)
  }
  if (isColumnWithAccessorKey(column)) {
    return column.accessorKey as DataKey
  }
}

const DOUBLE_CURLY_PLACEHOLDER = /\{\{([^}]+)\}\}/g

export const extractDoubleCurlyBracketPlaceholders = <DataKey extends string>(...texts: string[]): DataKey[] => {
  const keys: DataKey[] = []
  for (const text of texts) {
    DOUBLE_CURLY_PLACEHOLDER.lastIndex = 0
    for (const match of text.matchAll(DOUBLE_CURLY_PLACEHOLDER)) {
      keys.push(match[1].trim() as DataKey)
    }
  }
  return uniq(keys)
}

export const replaceDoubleCurlyBracketPlaceholders = (
  theme: Theme,
  text: string,
  placeholders: Record<string, ReactElement | string>,
  missingValue?: string
): ReactNode => {
  const textParts = text.split(DOUBLE_CURLY_PLACEHOLDER)
  if (textParts.length === 1) {
    if (text in placeholders) {
      return placeholders[text] ?? missingValue ?? text
    }
    return text
  }
  let allStrings = true
  const parts = textParts.map<ReactElement | string>((textPart) => {
    if (textPart in placeholders) {
      const part = placeholders[textPart] ?? missingValue ?? textPart
      if (isValidElement(part)) {
        allStrings = false
      }
      return part
    }
    return textPart
  })

  if (allStrings) {
    return parts.join('')
  }

  return (
    <StyledCellContainer justifyContent="start" gap={theme.spacing(1)}>
      {parts}
    </StyledCellContainer>
  )
}

export const getBodyCellFormattedValue = <DataKey extends string>({
  defaults,
  dataKey,
  elementDescription,
  alignProp,
  value,
  textVariant,
  theme
}: {
  defaults: ChartCardDefaults
  dataKey: DataKey
  elementDescription: ElementDescriptionMetric | ElementDescriptionDimension
  alignProp: CellAlignment | undefined
  value: ChartDataValue | undefined
  textVariant: 'h4' | 'body2'
  theme: Theme
}): { value: ReactElement | string | undefined; align: CellAlignment } => {
  const { type, descriptions } = elementDescription
  switch (type) {
    case 'currency':
    case 'number':
    case 'percentage':
      return {
        value: formatMetricValue(dataKey, elementDescription, value, defaults.table.format),
        align: alignProp ?? 'right'
      }
    case 'date':
      return { value: formatDateValue(dataKey, elementDescription, value), align: alignProp ?? 'left' }
    case 'string':
    case 'variant': {
      const stringValue = value == null ? undefined : String(value)
      const elementDescriptionValue = stringValue == null ? undefined : descriptions?.[stringValue]
      const { label: mappedLabel, color, pattern, assetUrl } = elementDescriptionValue ?? {}
      const valueText =
        value == null || value === ''
          ? mappedLabel ?? stringValue
          : getDimensionLabel(elementDescription, value, dataKey)
      let decoration: ReactNode
      if (color != null) {
        decoration = <ChartBadge key="badge" color={color} pattern={pattern} variant="darker" />
      }
      if (assetUrl != null) {
        decoration = [decoration, <StyledImage key="asset" src={assetUrl} alt="asset" height="24" width="24" />]
      }
      if (decoration == null) {
        return { value: valueText, align: alignProp ?? 'left' }
      }
      return {
        value: (
          <StyledCellContainer width="100%" justifyContent="start" gap={theme.spacing(1)}>
            <StyledPaddedDecorationContainer>{decoration}</StyledPaddedDecorationContainer>
            <StyledBodyText variant={textVariant} textAlign={alignProp ?? 'left'}>
              {valueText}
            </StyledBodyText>
          </StyledCellContainer>
        ),
        align: alignProp ?? 'left'
      }
    }
    default:
      errorLocal('TableBodyCellContent: Unknown element description type', { type, elementDescription })
      assertNever(type)
      return { value: undefined, align: alignProp ?? 'left' }
  }
}

export const getMetricsOrDimensionsMap = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  context: ChartCardTableBodyCellContextResolved<DataKey, DataRow>,
  metricsOrDimensions: DataKey[],
  textVariant: 'h4' | 'body2',
  theme: Theme,
  defaults: ChartCardDefaults
): Record<string, ReactElement | string> => {
  const row = context.row
  const { elementsDescriptionsMap } = context.meta.chartCard
  const map: Record<string, ReactElement | string> = {}
  for (const dataKey of metricsOrDimensions) {
    const value = row.original[dataKey]
    if (value == null) {
      continue
    }
    const elementDescription = elementsDescriptionsMap[dataKey] as ColumnElementDescription

    if (elementDescription == null) {
      if (typeof value === 'number') {
        map[dataKey] = formatMetricValue(dataKey, { type: 'number' }, value, defaults.table.format)
        continue
      }
      if (typeof value === 'string') {
        map[dataKey] = value
        continue
      }
      continue
    }

    const { value: cellValue } =
      getBodyCellFormattedValue({
        defaults,
        dataKey,
        elementDescription,
        alignProp: undefined,
        value,
        textVariant,
        theme
      }) ?? {}
    if (cellValue != null) {
      map[dataKey] = cellValue
    }
  }
  return map
}

export const calculateCSSPadding = (padding: Partial<Margin> | undefined, paddingChanges?: Partial<Margin>): string => {
  return [
    (padding?.top ?? 0) + (paddingChanges?.top ?? 0),
    (padding?.right ?? 0) + (paddingChanges?.right ?? 0),
    (padding?.bottom ?? 0) + (paddingChanges?.bottom ?? 0),
    (padding?.left ?? 0) + (paddingChanges?.left ?? 0)
  ]
    .map((value) => `${value}px`)
    .join(' ')
}

export const calculateTotalHorizontalPadding = (
  padding: Partial<Margin> | undefined,
  paddingChanges?: Partial<Margin>
): number => (padding?.right ?? 0) + (paddingChanges?.right ?? 0) + (padding?.left ?? 0) + (paddingChanges?.left ?? 0)

export const getFilteredOriginalRows = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  table: ChartCardTable<DataKey, DataRow>
): DataRow[] => table.getFilteredRowModel().rows.map((row) => row.original)

export const getOriginalRows = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  table: ChartCardTable<DataKey, DataRow>
): DataRow[] => table.getCoreRowModel().rows.map((row) => row.original)

export const getFooterMetricAggregation = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  metric: MetricChoice<DataKey, DataRow>,
  metricsMap: MetricsMap<DataKey, DataRow>
): ChartCardTableColumnDefBase<DataKey, DataRow>['footer'] => {
  return (context) => {
    const { table, meta } = context
    // Server-fetch footers must use totals from the dedicated totals fetch, not the page rows.
    if (meta?.chartCard?.preferTotalsData === true) {
      return meta.chartCard.total
    }

    const originalRows = getOriginalRows(table)
    const filteredRows = getFilteredOriginalRows(table)
    const total = aggregateMetricRespectingRecalculate(originalRows, metric, metricsMap)

    // Recalculate formulas on an empty set evaluate as ratio-of-zeros → 0, which
    // looks like a real zero ratio. Skip the dual footer only for those metrics;
    // additive columns (e.g. cost) should still show filteredTotal vs total.
    if (metric.recalculate === true && filteredRows.length === 0) {
      return total
    }

    const filteredTotal = aggregateMetricRespectingRecalculate(filteredRows, metric, metricsMap)
    return filteredTotal === total ? total : { total, filteredTotal }
  }
}

/**
 * Resolves footer totals and color-scale ranges for server-fetched tables.
 * When `serverTotals` is missing, returns empty totals (never page-derived sums)
 * and keeps page-derived min/max as a temporary fallback until the server responds.
 * When present, recalculates formula metrics (ROAS, CPA, …) from server dependency totals
 * and uses server min/max for color-scale contrast across the full result set.
 */
export const resolveServerFetchTotalsAndRange = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  isServerFetch,
  serverTotals,
  totalsAndRangeDataFromRows,
  columns,
  metricsMap
}: {
  isServerFetch: boolean
  serverTotals: TotalsAndRangeData<DataKey> | undefined
  totalsAndRangeDataFromRows: TotalsAndRangeData<DataKey>
  columns: Array<ChartCardTableColumnDef<DataKey, DataRow>>
  metricsMap: MetricsMap<DataKey, DataRow>
}): TotalsAndRangeData<DataKey> => {
  if (serverTotals == null) {
    if (isServerFetch) {
      return {
        totals: {},
        min: totalsAndRangeDataFromRows.min,
        max: totalsAndRangeDataFromRows.max
      }
    }
    return totalsAndRangeDataFromRows
  }

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- synthetic row for formula eval from totals map
  const syntheticRow = { ...serverTotals.totals } as DataRow
  const totals = { ...serverTotals.totals }

  for (const column of columns) {
    const dataKey = getColumnDataKey<DataKey, DataRow>(column)
    if (dataKey == null) {
      continue
    }
    const metric = metricsMap[dataKey]
    if (metric?.formula == null || metric.recalculate !== true) {
      continue
    }
    const value = aggregateMetricRespectingRecalculate([syntheticRow], metric, metricsMap)
    if (value != null) {
      totals[dataKey] = value
      syntheticRow[dataKey] = value as DataRow[DataKey]
    }
  }

  return {
    totals,
    min: serverTotals.min,
    max: serverTotals.max
  }
}

export const isFilteredTotal = (total: any): total is FilteredTotal => {
  return total != null && typeof total === 'object' && 'total' in total && 'filteredTotal' in total
}

export const getInitialSortingFromChartCardTableColumns = <
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
>({
  useDefaultSorting,
  columns,
  elementsDescriptionsMap
}: {
  useDefaultSorting: boolean
  columns: Array<ChartCardTableColumnDef<DataKey, DataRow>>
  elementsDescriptionsMap: ElementsDescriptionsMap<DataKey>
}): SortingState => {
  const sortingColumns = columns.filter(
    ({ meta, enableSorting }) => meta?.chartCard?.sort != null && enableSorting !== false
  )

  if (sortingColumns.length > 0) {
    return sortingColumns.map((column) => ({
      id: getColumnId(column),
      desc: column.meta?.chartCard?.sort === 'desc'
    })) as SortingState
  }

  if (!useDefaultSorting) {
    return []
  }

  return columns
    .filter(isColumnWithAccessorKey)
    .map((column) => ({
      id: getColumnId(column),
      elementDescription: elementsDescriptionsMap[column.accessorKey as DataKey]
    }))
    .filter(({ elementDescription }) => elementDescriptionIsDimension(elementDescription))
    .map(({ id }) => ({
      id,
      desc: false
    })) as SortingState
}

/** Max columns auto-pinned on initial render (left + right); manual pinning is unrestricted. */
export const DEFAULT_AUTO_PINNED_COLUMNS_LIMIT = 3

const columnHasExplicitPinPosition = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  column: ChartCardTableColumnDef<DataKey, DataRow>
): boolean => {
  const { pin, fixedPin } = column.meta?.chartCard ?? {}
  return fixedPin === 'left' || fixedPin === 'right' || pin === 'left' || pin === 'right'
}

const getPinnedFromChartCardTableColumn = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  column,
  elementsDescriptionsMap,
  allowColumnPinning,
  useDefaultPinning,
  autoPinnedCount
}: {
  column: ChartCardTableColumnDef<DataKey, DataRow>
  elementsDescriptionsMap: ElementsDescriptionsMap<DataKey>
  allowColumnPinning: boolean
  useDefaultPinning: boolean
  autoPinnedCount: number
}): [string, ColumnPinningPosition] | null => {
  const id = getColumnId(column)
  const { pin, fixedPin } = column.meta?.chartCard ?? {}
  if (typeof fixedPin === 'string' && fixedPin !== 'no') {
    return [id, fixedPin]
  }
  if (pin != null) {
    return [id, pin]
  }
  if (!useDefaultPinning || !allowColumnPinning) {
    return null
  }
  if (autoPinnedCount >= DEFAULT_AUTO_PINNED_COLUMNS_LIMIT) {
    return null
  }
  if (isColumnWithAccessorKey(column)) {
    const elementDescription = elementsDescriptionsMap[column.accessorKey as DataKey] as ColumnElementDescription
    if (elementDescription != null) {
      const { type } = elementDescription
      switch (type) {
        case 'date':
        case 'string':
        case 'variant':
          return [id, 'left']
        case 'number':
        case 'currency':
        case 'percentage':
          return null
        default:
          assertNever(type)
          return null
      }
    }
  }
  return null
}

export const getInitialPinningsFromChartCardTableColumns = <
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
>({
  useDefaultPinning,
  columns,
  elementsDescriptionsMap,
  allowColumnPinning,
  allowRowsSelection
}: {
  useDefaultPinning: boolean
  columns: Array<ChartCardTableColumnDef<DataKey, DataRow>>
  elementsDescriptionsMap: ElementsDescriptionsMap<DataKey>
  allowColumnPinning: boolean
  allowRowsSelection: boolean
}): ColumnPinningState => {
  const columnPinning: ColumnPinningState = allowRowsSelection ? { left: [ROW_SELECTION_COLUMN_ID] } : {}
  const shouldUseDefaultPinning = useDefaultPinning && allowColumnPinning && !columns.some(columnHasExplicitPinPosition)
  let autoPinnedCount = 0

  for (const column of columns) {
    const pinResult = getPinnedFromChartCardTableColumn<DataKey, DataRow>({
      column,
      elementsDescriptionsMap,
      allowColumnPinning,
      useDefaultPinning: shouldUseDefaultPinning,
      autoPinnedCount
    })
    if (pinResult == null) {
      continue
    }

    const [id, pinned] = pinResult
    if (pinned === 'left') {
      columnPinning.left = [...(columnPinning.left ?? []), id]
      if (shouldUseDefaultPinning) {
        autoPinnedCount += 1
      }
    } else if (pinned === 'right') {
      columnPinning.right = [...(columnPinning.right ?? []), id]
      if (shouldUseDefaultPinning) {
        autoPinnedCount += 1
      }
    }
  }

  return columnPinning
}

export const getInitialColumnOrderFromChartCardTableColumns = <
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
>(
  columns: Array<ChartCardTableColumnDef<DataKey, DataRow>>,
  columnPinning: ColumnPinningState
): ColumnOrderState => {
  const left = columnPinning.left ?? []
  const right = columnPinning.right ?? []
  return [
    ...left,
    ...columns.map((column) => getColumnId(column)).filter((id) => !left.includes(id) && !right.includes(id)),
    ...right
  ]
}

export const getInitialColorScaleColumnMapFromChartCardTableColumns = <
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
>(
  columns: Array<ChartCardTableColumnDef<DataKey, DataRow>>,
  elementsDescriptionsMap: ElementsDescriptionsMap<DataKey>
): ColorScaleColumnMap =>
  columns.reduce<ColorScaleColumnMap>((acc, column) => {
    if (column.meta?.chartCard == null) {
      return acc
    }
    const id = getColumnId(column)
    const { colorScale } = column.meta.chartCard
    if (colorScale === 'no') {
      return acc
    }
    if (typeof colorScale === 'boolean') {
      acc[id] = {
        display: colorScale
      }
      return acc
    }
    if (typeof colorScale === 'object') {
      acc[id] = colorScale
      return acc
    }
    if (!isColumnWithAccessorKey(column)) {
      return acc
    }
    const description = elementsDescriptionsMap[column.accessorKey as DataKey]
    const color = description?.color == null ? undefined : { color: description?.color }
    if (description?.type === 'number' || description?.type === 'currency' || description?.type === 'percentage') {
      acc[id] = {
        display: false,
        ...color
      }
    }
    return acc
  }, {})

export const buildKey = (prefix: string, columnsIds: string[], pinning: ColumnPinningState): string => {
  const left = pinning.left ?? []
  const right = pinning.right ?? []
  const center = columnsIds.filter((id) => !left.includes(id) && !right.includes(id))
  return `${prefix}:${left.join('|')}:${center.join('|')}:${right.join('|')}`
}

export const getHeaderContext = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  props: TableHeadCellProps<DataKey, DataRow>,
  columnDnDDragHandle?: DragHandleBinding
): ChartCardTableHeaderContextResolved<DataKey, DataRow> | null => {
  const {
    header,
    elementsDescriptionsMap,
    rowSelection,
    sorting,
    setSort,
    columnPinning,
    columnFilters,
    colorScaleColumnMap,
    setDisplayColorScale,
    useColumnsDecorations,
    allowColumnFiltering,
    allowDateFiltering,
    onOpenColumnFilter,
    allowColumnResizing,
    allowColumnAutoResizing,
    allowColumnPinning,
    allowColumnReordering,
    dispatch,
    t
  } = props

  const { isPlaceholder, getContext } = header

  if (isPlaceholder) {
    return null
  }

  const rawContext = getContext()
  const columnId = rawContext.column.id
  const allowResizing = allowColumnResizing && rawContext.column.columnDef.meta?.chartCard?.fixedSize !== true
  const allowAutoResizing = allowResizing && allowColumnAutoResizing

  const context: ChartCardTableHeaderContextResolved<DataKey, DataRow> = {
    ...rawContext,
    meta: {
      chartCard: {
        elementsDescriptionsMap,
        rowSelection,
        sorting,
        columnPinning,
        columnFilters,
        setSort,
        colorScale: colorScaleColumnMap[columnId],
        setDisplayColorScale,
        useColumnsDecorations,
        allowColumnFiltering,
        allowDateFiltering,
        onOpenColumnFilter,
        allowColumnResizing: allowResizing,
        allowColumnAutoResizing: allowAutoResizing,
        allowColumnPinning,
        allowColumnReordering,
        columnDnDDragHandle,
        dispatch,
        t
      }
    }
  }

  return context
}

export function getHeaderLabelData(context: null): HeaderLabelDataWithoutElement
export function getHeaderLabelData<DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  context: ChartCardTableHeaderContextResolved<DataKey, DataRow>
): HeaderLabelDataWithElement
export function getHeaderLabelData<DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  context: ChartCardTableHeaderContextResolved<DataKey, DataRow> | null
): HeaderLabelData
export function getHeaderLabelData<DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  context: ChartCardTableHeaderContextResolved<DataKey, DataRow> | null
): HeaderLabelData {
  if (context == null) {
    return {}
  }
  const { meta: contextMeta, ...rawContext } = context

  const { column } = rawContext

  const {
    id: columnId,
    columnDef: { meta: columnMeta, header: columnHeader }
  } = column

  const dataKey = getColumnDataKey<DataKey, DataRow>(column)

  const headerContent = typeof columnHeader === 'function' ? columnHeader(context) : columnHeader

  const { elementsDescriptionsMap, columnPinning } = contextMeta?.chartCard ?? {}

  const pinned = getPinPositionForColumnId(columnPinning, columnId)

  const { label, color, pattern, align } = columnMeta?.chartCard ?? {}

  const elementDescription =
    dataKey == null
      ? undefined
      : (elementsDescriptionsMap?.[dataKey] as ElementDescriptionMetric | ElementDescriptionDimension | undefined)

  const isMetric = elementDescriptionIsMetric(elementDescription)

  const textAlign: CellAlignment = align ?? (isMetric ? 'right' : 'left')
  const defaultSortDirection = columnMeta?.chartCard?.defaultSortDirection ?? (isMetric ? 'desc' : 'asc')
  let justifyContent: 'start' | 'end' | 'center' = 'start'
  if (textAlign === 'right') {
    justifyContent = 'end'
  } else if (textAlign === 'center') {
    justifyContent = 'center'
  }

  const width = column.getSize()

  if (isValidElement(headerContent)) {
    return { element: headerContent, textAlign, justifyContent, defaultSortDirection, width, pinned }
  } else {
    const justLabel = (
      <StyledHeaderLabel key="header-label" textAlign={textAlign}>
        {headerContent ?? label ?? columnId}
      </StyledHeaderLabel>
    )

    const decoration =
      color != null && contextMeta?.chartCard?.useColumnsDecorations ? (
        <ChartBadge color={color} pattern={pattern} variant="darker" />
      ) : null

    const labelAndDecoration =
      decoration == null ? (
        justLabel
      ) : (
        <StyledCellContainer>
          {decoration}
          {justLabel}
        </StyledCellContainer>
      )

    return {
      title: headerContent ?? label ?? columnId,
      textAlign,
      justifyContent,
      defaultSortDirection,
      element: labelAndDecoration,
      width,
      pinned
    }
  }
}

export function getTableActionContext(table: null): null
export function getTableActionContext<DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  table: ChartCardTable<DataKey, DataRow>
): ChartCardTableActionContext<DataKey, DataRow>
export function getTableActionContext<DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  table: ChartCardTable<DataKey, DataRow> | null
): ChartCardTableActionContext<DataKey, DataRow> | null
export function getTableActionContext<DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  table: ChartCardTable<DataKey, DataRow> | null
): ChartCardTableActionContext<DataKey, DataRow> | null {
  if (table == null) {
    return null
  }
  return {
    type: 'table',
    getRows: () => table.getPrePaginationRowModel().rows,
    getVisibleRows: () => table.getRowModel().rows,
    getFilteredRows: () => table.getFilteredRowModel().rows,
    getSelectedRows: () => table.getSelectedRowModel().rows
  }
}

export function getTableRowActionContext<DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  table: ChartCardTable<DataKey, DataRow>,
  row: Row<DataRow>
): ChartCardTableRowActionContext<DataKey, DataRow> | null {
  if (table == null) {
    return null
  }
  return {
    type: 'tableRow',
    row,
    getRows: () => table.getPrePaginationRowModel().rows,
    getVisibleRows: () => table.getRowModel().rows,
    getFilteredRows: () => table.getFilteredRowModel().rows,
    getSelectedRows: () => table.getSelectedRowModel().rows
  }
}

export const getActionClickHandler = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  id: string,
  onClick: ChartCardActionOnClick<DataKey, DataRow, ChartCardTableActionContext<DataKey, DataRow>>,
  table: ChartCardTable<DataKey, DataRow> | null
): (() => void) => {
  return () => {
    const context = getTableActionContext(table)
    if (context != null) {
      onClick(id, context)
    }
  }
}

export const getRowActionClickHandler = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  id: string,
  onClick: ChartCardActionOnClick<DataKey, DataRow, ChartCardTableRowActionContext<DataKey, DataRow>>,
  row: Row<DataRow>,
  table: ChartCardTable<DataKey, DataRow>
): (() => void) => {
  return () => {
    const context = getTableRowActionContext(table, row)
    if (context != null) {
      onClick(id, context)
    }
  }
}
